import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Banner, Button, Card, PageBody, PageHeader, Well } from "../ui/Field";

export default function RecoveryCodes() {
  const [status, setStatus] = useState<{ unused_count: number; last_used_at: number | null } | null>(null);
  const [fresh, setFresh] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ unused_count: number; last_used_at: number | null }>("/api/auth/recovery-codes").then(setStatus).catch((e) => setErr((e as Error).message));
  }, []);

  async function generate() {
    if (status && status.unused_count > 0 && !confirm("This will invalidate your existing recovery codes. Continue?")) return;
    try {
      const r = await api.post<{ codes: string[] }>("/api/auth/recovery-codes/generate");
      setFresh(r.codes);
      const s = await api.get<{ unused_count: number; last_used_at: number | null }>("/api/auth/recovery-codes");
      setStatus(s);
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ Account · Recovery"
        title={<>Recovery <em>codes</em></>}
        description="Single-use codes for when every passkey is lost. Save them somewhere offline."
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="R1" title="Status">
        <p className="text-[14px] text-[var(--color-ink)]">
          You have <span className="display text-2xl text-[var(--color-accent)] align-baseline">{status?.unused_count ?? "—"}</span> unused recovery codes.
        </p>
        <p className="text-[12.5px] text-[var(--color-muted)] mt-2 max-w-md">
          Generating a new batch invalidates any unused codes from the previous batch.
        </p>
        <div className="mt-4">
          <Button onClick={generate}>{status && status.unused_count > 0 ? "Regenerate codes" : "Generate codes"}</Button>
        </div>
      </Card>

      {fresh && (
        <Card
          number="R2"
          title="Save these now — they will not be shown again"
          className="mt-6"
        >
          <Well>
            <ol className="grid grid-cols-2 gap-x-8 gap-y-2 font-mono text-[15px] text-[var(--color-ink)]">
              {fresh.map((c, i) => (
                <li key={c} className="flex items-center gap-3">
                  <span className="font-mono text-[11px] text-[var(--color-muted)] w-5">{String(i + 1).padStart(2, "0")}</span>
                  <code>{c}</code>
                </li>
              ))}
            </ol>
          </Well>
          <p className="text-[12px] text-[var(--color-muted)] mt-3">Each code can be used once. Treat them like backup keys.</p>
        </Card>
      )}
    </PageBody>
  );
}
